import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  ParameterGroupDto,
  ParameterGroupEditDto
} from 'src/store/parametergroups/parametergroup';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum ParameterGroupTypes {
  FetchAll = 'parameterGroups/FetchAll',
  Add = 'parameterGroups/Add',
  Update = 'parameterGroups/Update',
  Delete = 'parameterGroups/Delete',
  Failure = 'parameterGroups/Failure'
}

export type ParameterGroupAction =
  | { type: ParameterGroupTypes.FetchAll; parameterGroups: ParameterGroupDto[] }
  | { type: ParameterGroupTypes.Add; parameterGroup: ParameterGroupDto }
  | { type: ParameterGroupTypes.Update; parameterGroup: ParameterGroupDto }
  | { type: ParameterGroupTypes.Delete; parameterGroup: ParameterGroupDto }
  | { type: ParameterGroupTypes.Failure; errors: any };

export const actionCreators = {
  fetchParameterGroups: (
    parameterGroups: ParameterGroupDto[]
  ): ParameterGroupAction => ({
    type: ParameterGroupTypes.FetchAll,
    parameterGroups
  }),
  addParameterGroup: (
    parameterGroup: ParameterGroupDto
  ): ParameterGroupAction => ({
    type: ParameterGroupTypes.Add,
    parameterGroup
  }),
  updateParameterGroup: (
    parameterGroup: ParameterGroupDto
  ): ParameterGroupAction => ({
    type: ParameterGroupTypes.Update,
    parameterGroup
  }),
  deleteParameterGroup: (
    parameterGroup: ParameterGroupDto
  ): ParameterGroupAction => ({
    type: ParameterGroupTypes.Delete,
    parameterGroup
  }),
  parameterGroupRequestFailure: (errors: any): ParameterGroupAction => ({
    type: ParameterGroupTypes.Failure,
    errors
  })
};

export const fetchParameterGroups = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ParameterGroupTypes.FetchAll));
    const { ok, data, error } = await getRequest<ParameterGroupDto[]>(
      '/parameterGroups'
    );
    dispatch(requestEnd(ParameterGroupTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchParameterGroups(data))
      : dispatch(actionCreators.parameterGroupRequestFailure(error));
  };
};

export const createParameterGroup = (parameterGroup: ParameterGroupEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      '/parameterGroups',
      parameterGroup
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('parameterGroup.created')));
      return dispatch(
        actionCreators.addParameterGroup({ ...parameterGroup, id: data })
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.parameterGroupRequestFailure(error));
  };
};

export const editParameterGroup = (
  id: number,
  parameterGroup: ParameterGroupEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(
      `/parameterGroups/${id}`,
      parameterGroup
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('parameterGroup.updated')));
      return dispatch(
        actionCreators.updateParameterGroup(parameterGroup as ParameterGroupDto)
      );
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.parameterGroupRequestFailure(error));
  };
};

export const deleteParameterGroup = (
  id: number,
  parameterGroup: ParameterGroupDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(
      `/parameterGroups/${id}`,
      parameterGroup
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('parameterGroup.deleted')));
      return dispatch(actionCreators.deleteParameterGroup(parameterGroup));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.parameterGroupRequestFailure(error));
  };
};
